/* eslint-disable indent */
/* eslint-disable no-nested-ternary */
import React, { useState, memo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import { Button, ButtonGroup, Card, FormControl, FormHelperText, Tooltip, Typography } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { AccessControl } from '../../../../../../components'

import {
  deleteFlowTaskInteractionVariable,
  updateFlowTaskInteractionVariable,
  cacheDeleteFlowTaskInteractionVariable,
  cacheUpdateFlowTaskInteractionVariable
} from '../../../../../../actions'
import { checkErrorResponse, Logger, LoggerTypes } from '../../../../../../utils'
import { Icon, iconNames } from '../../../../../../components'
import { colorTypes, interactionOperations, userRightTypes } from '../../../../../../constants'
import './style.scss'

const defaultValue = {
  MINIMUM: 999,
  MAXIMUM: 1
}

/**
 * Display the Interaction object Card either for a `variable` or a `resource`
 *
 * @param {object} currentProject Current project object
 * @param {object} currentScenario Current scenario object
 * @param {object} currentFlowGroup Current flowGroup object
 * @param {object} currentTaskInstance Current task instance object
 * @param {object} currentInteraction  Ex: { variable_id: 'xxx-UUID-xx', operation: 'PLUS', value: 4, type: 'ON_START' }
 * @returns {Dialog}
 */
export const FlowElementTaskInteractionVariableStartCard = memo(
  ({
    currentProject,
    currentScenario,
    currentFlowGroup,
    currentTaskInstance,
    currentInteraction
    // refreshAfterSaving
  }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()

    const pid = currentProject?.id || null
    const sid = currentScenario?.id || null
    const fid = currentFlowGroup?.id || null
    const tid = currentTaskInstance?.id || null

    const [_currentInteraction] = useState(currentInteraction)
    const [init_quantity] = useState(_currentInteraction.variable.initial_value)
    const [quantity, setQuantity] = useState(Math.abs(_currentInteraction?.value))
    const [name] = useState(_currentInteraction.variable.name)
    const [operation] = useState(_currentInteraction.operation)

    // -- errors handlers
    const [errors, setErrors] = useState(null)
    const hasError = Boolean(errors && (errors?.value || errors?.quantity))

    const onUpdate = (newQuantity) => {
      let interaction = {
        ...currentInteraction,
        operation
      }

      interaction.value = Math.abs(newQuantity)
      interaction.operation = interactionOperations.MINUS

      Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskInteractionVariableStartCard.onUpdate()', { interaction })

      setErrors([])

      dispatch(updateFlowTaskInteractionVariable(pid, sid, fid, tid, interaction))
        .then((res) => {
          Logger.trace(
            LoggerTypes.CONTROLLER,
            'FlowElementTaskInteractionVariableStartCard.onUpdate().dispatch(...).then()',
            {
              res
            }
          )

          // -- remove the previous errors
          setErrors(null)

          // -- reload the FlowGroups data.
          // if (refreshAfterSaving && typeof refreshAfterSaving === 'function') {
          //   refreshAfterSaving()
          // }
          dispatch(cacheUpdateFlowTaskInteractionVariable(currentFlowGroup, tid, interaction))
        })
        .catch((res) => {
          Logger.trace(
            LoggerTypes.WARN,
            'FlowElementTaskInteractionVariableStartCard.onUpdate().dispatch(...).catch()',
            { res }
          )

          checkErrorResponse(res, enqueueSnackbar, t)

          if (res?.response?.data?.fields) {
            if (res?.response?.data?.fields?._schema) {
              setErrors({ quantity: res.response.data.fields._schema })
            } else {
              setErrors(res.response.data.fields)
            }
          }
        })
    }

    const onDelete = () => {
      Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskInteractionVariableStartCard.onDelete()', {
        currentInteraction
      })

      dispatch(deleteFlowTaskInteractionVariable(pid, sid, fid, tid, currentInteraction.id))
        .then((res) => {
          Logger.trace(
            LoggerTypes.CONTROLLER,
            'FlowElementTaskInteractionVariableStartCard.onDelete().dispatch(...).then()',
            {
              res
            }
          )

          // -- reload the FlowGroups data.
          // if (refreshAfterSaving && typeof refreshAfterSaving === 'function') {
          //   refreshAfterSaving()
          // }
          dispatch(cacheDeleteFlowTaskInteractionVariable(currentFlowGroup, tid, currentInteraction))
        })
        .catch((res) => {
          Logger.trace(
            LoggerTypes.WARN,
            'FlowElementTaskInteractionVariableStartCard.onDelete().dispatch(...).catch()',
            { res }
          )
          checkErrorResponse(res, enqueueSnackbar, t)
          if (res?.response?.data) {
            setErrors(res.response.data)
          }
        })
    }

    const onQuantityPlus = (e) => {
      e.preventDefault()
      const newQuantity = quantity - 1

      Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskInteractionVariableStartCard.onQuantityPlus()', {
        newQuantity,
        quantity
      })

      setQuantity(newQuantity)

      onUpdate(newQuantity)
    }

    const onQuantityMinus = (e) => {
      e.preventDefault()
      const newQuantity = quantity + 1

      Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskInteractionVariableStartCard.onQuantityMinus()', {
        newQuantity,
        quantity
      })

      setQuantity(newQuantity)

      onUpdate(newQuantity)
    }

    Logger.trace(LoggerTypes.CONTAINER, 'FlowElementTaskInteractionVariableStartCard.render()', {
      currentInteraction,
      name,
      quantity,
      operation,
      errors
    })

    return (
      <Card className="FlowElementTaskInteractionVariableStartCard common-card">
        <FormControl error={hasError} style={{ display: 'block' }}>
          <div>
            <div className="mainC">
              <div className="leftC">
                {/* -- name (initial_value) -- */}
                <Typography
                  className={`common-card-header-name name ${operation ? '' : 'name-resource'}`}
                  color={hasError ? colorTypes.ERROR : colorTypes.PRIMARY}
                >
                  {`${name} (${init_quantity})` || 'N/A'}
                </Typography>

                {/* -- quantity -- */}
                {operation ? (
                  operation === interactionOperations.PLUS ? (
                    <Typography
                      className="common-card-header-name operation"
                      color={hasError ? colorTypes.ERROR : colorTypes.PRIMARY}
                    >
                      <Icon name={iconNames.PLUS} size={9} />
                    </Typography>
                  ) : (
                    <Typography
                      className="common-card-header-name operation"
                      color={hasError ? colorTypes.ERROR : colorTypes.PRIMARY}
                    >
                      <Icon name={iconNames.MINUS} size={9} />
                    </Typography>
                  )
                ) : null}
                <Typography
                  className="common-card-header-name quantity"
                  color={hasError ? colorTypes.ERROR : colorTypes.PRIMARY}
                >
                  {`${String(Math.abs(quantity))}`}
                </Typography>
              </div>

              <div className="rightC">
                {/* -- <[-]|[+]> -- */}
                <AccessControl userPermissions={[userRightTypes.SCENARIO_ATTRIBUTES_EDIT]} renderNoAccess={() => <></>}>
                  <ButtonGroup size="small" aria-label="small outlined button group">
                    {/* -- Button: <[-] -- */}
                    {/* <Button error={hasError} disabled={quantity >= defaultValue.MINIMUM} onClick={onQuantityMinus}> */}
                    <Button disabled={quantity >= defaultValue.MINIMUM} onClick={onQuantityMinus}>
                      <Icon name={iconNames.MINUS} size={8} color={colorTypes.PRIMARY} />
                    </Button>
                    {/* -- Button: [+]> -- */}
                    {/* <Button error={hasError} disabled={quantity <= defaultValue.MAXIMUM} onClick={onQuantityPlus}> */}
                    <Button disabled={quantity <= defaultValue.MAXIMUM} onClick={onQuantityPlus}>
                      <Icon name={iconNames.PLUS} size={8} color={hasError ? colorTypes.ERROR : colorTypes.PRIMARY} />
                    </Button>
                  </ButtonGroup>
                </AccessControl>

                {/* -- Button delete -- */}
                <AccessControl
                  userPermissions={[userRightTypes.SCENARIO_ATTRIBUTES_DELETE]}
                  renderNoAccess={() => <></>}
                >
                  <Tooltip title={t('common.delete')}>
                    <Button onClick={onDelete} className="btDelete" color="primary">
                      <DeleteOutlineIcon fontSize="small" />
                    </Button>
                  </Tooltip>
                </AccessControl>
              </div>
            </div>
          </div>
          <FormHelperText>{(errors && errors?.value) || (errors && errors?.quantity) || ''}</FormHelperText>
        </FormControl>
      </Card>
    )
  }
)

FlowElementTaskInteractionVariableStartCard.displayName = 'FlowElementTaskInteractionVariableStartCard'

FlowElementTaskInteractionVariableStartCard.propTypes = {
  currentProject: PropTypes.object.isRequired,
  currentScenario: PropTypes.object.isRequired,
  currentFlowGroup: PropTypes.object.isRequired,
  currentTaskInstance: PropTypes.object.isRequired,
  currentInteraction: PropTypes.object.isRequired
  // -- refresh after saving
  // refreshAfterSaving: PropTypes.func
}

FlowElementTaskInteractionVariableStartCard.defaultProps = {
  currentProject: null,
  currentScenario: null,
  currentFlowGroup: null,
  currentTaskInstance: null,
  currentInteraction: null // e.g: {id: 'xx-UUID-xx', operation: 'plus', quantity}
  // refreshAfterSaving: () => {}
}
